import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  OnDestroy,
  Output,
  HostListener,
  Renderer2,
  ElementRef,
  ViewChild,
} from '@angular/core';
import {
  BaseModalService,
  environment,
} from '@ems-gui/expense/util-web-infrastructure';
import { ExpenseService } from '@src/app/services/expense.service';
import { AuthService } from '@sec-spec/lib-ng-oauth';
import { DropzoneConfigInterface } from 'nxt-dropzone-wrapper';
import { DOCUMENT } from '@angular/common';
import { Action, Store } from '@ngrx/store';
import {
  AllModalActions,
  LayoutActions,
  selectAllowedToUpload,
  selectContinueTo,
  selectUserHasCreditCard,
  State,
} from '@ems-gui/expense/util-web-ngrx';
import { firstValueFrom, map, Subscription } from 'rxjs';
import { setCurrentModal } from '@libs/expense/util-web-ngrx/src/lib/+state/actions/all-modal.actions';

@Component({
  selector: 'ems-expense-summary',
  templateUrl: './expense-summary.component.html',
  styleUrls: ['./expense-summary.component.scss'],
})
export class ExpenseSummaryComponent implements OnInit, OnDestroy {
  @Input() recentSubmissions;
  @Input() submissionCounts;
  @Input() approvalCounts;
  @Input() isApprover;
  @Input() summaryLoading;
  @Input() expensesLoading;
  @Input() proxy = '';
  @Output() openModal = new EventEmitter();
  @Output() upload = new EventEmitter();
  @Output() uploaded: EventEmitter<any> = new EventEmitter<any>();
  @Output() receiptUploadSizeError: EventEmitter<any> = new EventEmitter<any>();
  // Dropzone elements
  @ViewChild('quickUploadCreditCardContainer')
  private quickUploadContainer: ElementRef;
  @ViewChild('quickUploadCreditCardLabel') private quickUploadLabel: ElementRef;
  @ViewChild('dropzoneContainer') private dropzoneContainer: ElementRef;
  @ViewChild('dropzone') private dropzone: ElementRef;
  @ViewChild('quickUploadCreditCardHolderMessage')
  private quickUploadCCHolderMessage: ElementRef;
  // Dashboard-action elements
  // @ViewChild('dropzone') private dropzone: ElementRef;

  public continueTo$ = this.store$.select(selectContinueTo);
  public creditCardUser$ = this.store$.select(selectUserHasCreditCard);
  protected allowedToUpload$ = this.store$.select(selectAllowedToUpload);
  protected ccUserInit = false;
  @ViewChild('oopDashboardContainer') private oopDashboardContainer: ElementRef;
  @ViewChild('oopDashboardButton') private oopDashboardButton: ElementRef;
  @ViewChild('oopDashboardIcon') private oopDashboardIcon: ElementRef;
  @ViewChild('oopDashboardLabel') private oopDashboardLabel: ElementRef;
  @ViewChild('oopCCHolderMessage') private oopCCHolderMessage: ElementRef;
  @ViewChild('oopDashboardCCHolderButton')
  private oopDashboardCCHolderButton: ElementRef;

  // TODO: Update this to use the correct value
  hasCreditCard: boolean;

  smallMaxScreenWidth = 767;
  screenHeight: number;
  screenWidth: number;

  get authorization() {
    const token = this.auth.getToken();
    const auth = token ? `Bearer ${token}` : null;
    return auth;
  }

  // Dashboard Upload
  public config: DropzoneConfigInterface = {
    url: `${environment.apiURL}/expenses/receipt`,
    method: 'post',
    clickable: false,
    maxFiles: null,
    autoProcessQueue: false, // stops dropzone from processing file automatically
    previewsContainer: false, // disables the preview
    headers: {
      Authorization: this.authorization,
      'x-proxy-for': this.proxy ? this.proxy : '',
    },
    paramName: 'receipt',
    autoReset: null,
    errorReset: null,
    cancelReset: null,
    thumbnailWidth: 320,
    thumbnailHeight: 560,
    acceptedFiles: 'image/jpg,image/jpeg,image/png,application/pdf',
  };

  protected setFileUpload(fileUploadAllowed = false) {
    this.dispatch(AllModalActions.setAllowUploads({ fileUploadAllowed }));
  }

  public file: File | FileList;

  protected async displayCreditCardDisclaimer() {
    this.onOpenModal('file-upload');
  }

  protected selectFile() {
    const fileHolder = document.createElement('input');
    fileHolder.type = 'file';
    fileHolder.accept = this.config.acceptedFiles;
    fileHolder.onchange = (event: Event) => this.uploadFileOnChange(event);
    fileHolder.click();
  }

  protected async canWeProceed() {
    return [
      await firstValueFrom(this.creditCardUser$),
      await firstValueFrom(this.allowedToUpload$),
    ];
  }

  public async uploadFileOnChange(event: Event) {
    const element = event.target || event.currentTarget;
    if (element instanceof HTMLInputElement) {
      if (element.files && element.files.length) {
        this.file = element.files[0];
        this.onUploadFile();
      }
    }
  }

  /**
   * Step 1 click if they are not CCUser then show list of files to select.
   * @param event
   */
  public async onClickFileSelect(event: MouseEvent) {
    const [ccUser, allowedToUpload] = await this.canWeProceed();

    if (!ccUser) return this.selectFile();

    const setToFalse = () => {
      this.setFileUpload(false);
      this.ccUserInit = true;
    };

    this.listenForStateChanges();

    if (!this.ccUserInit) setToFalse();
    else if (allowedToUpload) {
      setToFalse();
      return this.selectFile();
    }
    event.preventDefault();

    this.displayCreditCardDisclaimer();
  }

  protected onUploadFile() {
    this.upload.emit(this.file);
    this.file = undefined;
    this.ccUserInit = false;
  }

  protected listenForStateChanges() {
    this.subscriptions.push(
      this.continueTo$.subscribe(async (continueTo) => {
        if (continueTo !== '') return;

        this.unsubscribe();
        const [ccUser, allowedToUpload] = await this.canWeProceed();
        if (!ccUser || allowedToUpload) {
          if (this.file) return this.onUploadFile();
          this.selectFile();
        }
      })
    );
  }

  /**
   * Step 1 Drag and Drop
   * @param file
   */
  public async waitForConfirmation(file: File | FileList) {
    const ccUser = await firstValueFrom(this.creditCardUser$);
    if (!ccUser) {
      this.file = 'length' in file ? file[0] : file;
      return this.onUploadFile();
    }
    this.file = file;
    this.setFileUpload();
    this.listenForStateChanges();
    this.displayCreditCardDisclaimer();
  }

  /**
   * Classnames
   * Generates a list of classes based on inputs
   */
  classNames() {
    return {
      'no-approvals': !this.isApprover && !this.summaryLoading,
    };
  }

  hideFirstUseBlock = false;

  public constructor(
    public modalService: BaseModalService,
    private auth: AuthService,
    private expenseService: ExpenseService,
    private store$: Store<State>,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2
  ) {
    this.onResize();
  }

  protected dispatch(actionToDispatch: Action) {
    return this.store$.dispatch(actionToDispatch);
  }

  protected subscriptions: Subscription[] = [];

  protected unsubscribe() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnInit() {
    const cookieExists: boolean =
      this.document.cookie.includes('hideFirstUseBlock');
    if (!cookieExists) {
      this.document.cookie = 'hideFirstUseBlock=false';
    }

    this.hideFirstUseBlock = this.document.cookie.includes(
      'hideFirstUseBlock=true'
    );
    this.creditCardUser$.subscribe(
      (hasCreditCard) => (this.hasCreditCard = hasCreditCard)
    );
  }

  ngAfterViewInit() {
    // add in listeners here
    if (this.screenWidth <= this.smallMaxScreenWidth && this.hasCreditCard) {
      this.setUpSmallScreenElements();

      // set up event listeners
      this.renderer.listen(
        this.dropzoneContainer.nativeElement,
        'click',
        this.dropzoneContainerClick.bind(this)
      );
      this.renderer.listen(
        this.oopDashboardContainer.nativeElement,
        'click',
        this.dashboardContainerClick.bind(this)
      );
    }
  }

  setUpSmallScreenElements() {
    this.dropzone.nativeElement.style.setProperty('pointer-events', 'none');
    this.oopDashboardButton.nativeElement.style.setProperty(
      'pointer-events',
      'none'
    );
  }

  dropZoneClickCount = 0;
  public dropzoneContainerClick() {
    this.dropZoneClickCount++;

    if (this.dropZoneClickCount % 2 == 1) {
      this.styleDropzoneContainer();
    } else if (this.dropZoneClickCount % 2 == 0) {
      this.unstyleDropzoneContainer();
    }
  }

  private unstyleDropzoneContainer() {
    this.quickUploadLabel.nativeElement.style.setProperty(
      'display',
      'inline-block'
    );
    this.quickUploadCCHolderMessage.nativeElement.style.setProperty(
      'display',
      'none'
    );
    this.dropzone.nativeElement.style.setProperty('pointer-events', 'none');

    // styling for dropzone box
    this.quickUploadContainer.nativeElement.style.setProperty(
      'background',
      'hsl(0, 0%, 100%)'
    );
    this.quickUploadContainer.nativeElement.style.setProperty(
      'box-shadow',
      'none'
    );
  }

  private styleDropzoneContainer() {
    this.quickUploadLabel.nativeElement.style.setProperty('display', 'none');
    this.quickUploadCCHolderMessage.nativeElement.style.setProperty(
      'display',
      'inline-block'
    );
    this.dropzone.nativeElement.style.setProperty('pointer-events', 'auto');

    // styling for dropzone box
    this.quickUploadContainer.nativeElement.style.setProperty(
      'background',
      'linear-gradient(0deg, rgba(0, 133, 255, 0.06) 0%, rgba(0, 133, 255, 0.06) 100%), #FFF'
    );
    this.quickUploadContainer.nativeElement.style.setProperty(
      'box-shadow',
      '4px 4px 8px 0px rgba(0, 0, 0, 0.25)'
    );
  }

  dashboardClickCount = 0;
  public dashboardContainerClick() {
    this.dashboardClickCount++;
    if (this.dashboardClickCount % 2 == 1) {
      this.styleSelectedDashboardButtonsForCCHolder();
    } else if (this.dashboardClickCount % 2 == 0) {
      this.unstyleSelectedDashboardButtonsForCCHolder();
    }
  }

  private styleSelectedDashboardButtonsForCCHolder() {
    // hide elements
    this.oopDashboardIcon.nativeElement.style.setProperty('display', 'none');
    this.oopDashboardLabel.nativeElement.style.setProperty('display', 'none');

    // set up layout
    this.oopDashboardButton.nativeElement.style.setProperty('height', '129px');
    this.oopCCHolderMessage.nativeElement.style.setProperty(
      'align-self',
      'stretch'
    );
    this.oopDashboardButton.nativeElement.style.setProperty(
      'align-self',
      'stretch'
    );
    this.oopCCHolderMessage.nativeElement.style.setProperty('display', 'flex');
    this.oopDashboardButton.nativeElement.style.setProperty(
      'pointer-events',
      'auto'
    );
    this.oopDashboardCCHolderButton.nativeElement.style.setProperty(
      'display',
      'flex'
    );

    // set shadow and set color
    this.oopDashboardButton.nativeElement.style.setProperty(
      'box-shadow',
      '4px 4px 8px 0px rgba(0, 0, 0, 0.25)'
    );
    this.oopDashboardButton.nativeElement.style.setProperty(
      'background',
      'linear-gradient(0deg, rgba(0, 133, 255, 0.06) 0%, rgba(0, 133, 255, 0.06) 100%), #FFF'
    );
  }

  private unstyleSelectedDashboardButtonsForCCHolder() {
    this.oopDashboardIcon.nativeElement.style.setProperty(
      'display',
      'inline-block'
    );
    this.oopDashboardLabel.nativeElement.style.setProperty(
      'display',
      'inline-block'
    );

    this.oopCCHolderMessage.nativeElement.style.setProperty('display', 'none');
    this.oopDashboardButton.nativeElement.style.setProperty(
      'pointer-events',
      'none'
    );
    this.oopDashboardCCHolderButton.nativeElement.style.setProperty(
      'display',
      'none'
    );

    // reset shadow and reset color
    this.oopDashboardButton.nativeElement.style.setProperty(
      'box-shadow',
      'none'
    );
    this.oopDashboardButton.nativeElement.style.setProperty(
      'background',
      'hsl(0, 0%, 100%)'
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?) {
    this.screenHeight = window.innerHeight;
    this.screenWidth = window.innerWidth;
  }

  closeFirstUseBlock() {
    this.hideFirstUseBlock = true;
    this.document.cookie = 'hideFirstUseBlock=true';
  }

  openLink(link: string) {
    window.open(link, '_blank');
  }

  public async onOpenModal(modalId: string, id?: number) {
    this.store$.dispatch(
      AllModalActions.setCurrentModal({ currentModal: modalId })
    );
    if (modalId === 'credit-card-holder-alert') {
      this.dispatch(LayoutActions.openExpenseModal({ name: modalId }));
      this.dispatch(
        AllModalActions.setContinueTo({
          continueTo: 'file-upload',
        })
      );
    } else {
      this.openModal.emit({ name: modalId, id: id });
    }
  }

  public get requiredSubmissionCount() {
    const { draft, rejected } = this.submissionCounts;
    return draft.totalCount + rejected.totalCount;
  }

  public get pendingCount() {
    return this.submissionCounts.pending.totalCount;
  }

  public get submissionApprovedCount() {
    return this.submissionCounts.approved.totalCount;
  }

  public get unapprovedCount() {
    return this.approvalCounts.unapproved.totalCount;
  }

  public get overdueCount() {
    return this.approvalCounts.overdue.totalCount;
  }

  public get approvedCount() {
    return this.approvalCounts.approved.totalCount;
  }

  public onUploadError(args: any): void {
    if (args[1].status && args[1].expenseId) {
      this.uploaded.emit(args[1].expenseId);
    } else if (args[1].message?.match(/Upload limit/)) {
      this.receiptUploadSizeError.emit(args[1].message);
    }
  }

  public onUploadSuccess(args: any): void {
    if (!this.allowedToUpload$) return;
    if (args[1].status && args[1].expenseId) {
      this.uploaded.emit(args[1].expenseId);
    }
  }

  public getPaymentType(data) {
    if (data) {
      this.expenseService.getPaymentType(data);
    }
  }

  public ngOnDestroy() {
    this.unsubscribe();
  }
}
